import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCents, PLAN_LABELS, PLAN_PRICES_GROSS_MONTH_CENTS, PLANS, type Plan, type PlanModules } from '@kontor/shared';
import { ApiError } from '../api';
import { operatorApi } from './operatorApi';

type Choice = Plan | 'addon';
const CHOICES: { value: Choice; label: string }[] = [...PLANS.map((p) => ({ value: p, label: PLAN_LABELS[p] })), { value: 'addon', label: 'Nur als Zusatzmodul' }];
const dateTime = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
const choiceOf = (plan: Plan | null): Choice => plan ?? 'addon';
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);

/**
 * Pakete: Der Betreiber ordnet jedes Modul einem Paket zu, jederzeit (Änderungswunsch vom 17.10.2026). Ein größeres Paket enthält immer alles aus den kleineren. Die Änderung wirkt
 * sofort für alle Mandanten im Backoffice und auf der Webseite (plans.js holt sie von GET /meta).
 */
export function OperatorPlansPage() {
  const client = useQueryClient();
  const [draft, setDraft] = useState<Record<string, Choice>>({});
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const plans = useQuery({ queryKey: ['operator-plans'], queryFn: async () => (await operatorApi<{ data: PlanModules }>('GET', '/plans')).data });
  const data = plans.data;

  useEffect(() => {
    if (data) setDraft(Object.fromEntries(data.modules.map((m) => [m.key, choiceOf(m.plan)])));
  }, [data]);

  const dirty = useMemo(() => !!data && data.modules.some((m) => draft[m.key] !== undefined && draft[m.key] !== choiceOf(m.plan)), [data, draft]);
  const moved = useMemo(
    () => (data ? data.modules.filter((m) => draft[m.key] !== undefined && draft[m.key] !== choiceOf(m.plan)) : []),
    [data, draft],
  );
  const counts = (plan: Plan) => {
    if (!data) return 0;
    const rank = (p: Choice) => (p === 'addon' ? 99 : PLANS.indexOf(p));
    return data.modules.filter((m) => m.available && rank(draft[m.key] ?? choiceOf(m.plan)) <= PLANS.indexOf(plan)).length;
  };

  const save = useMutation({
    mutationFn: () => operatorApi<{ data: PlanModules }>('PUT', '/plans', { assignments: draft }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['operator-plans'] });
      client.invalidateQueries({ queryKey: ['meta'] });
      setMessage({ ok: true, text: 'Die Zuordnung gilt ab sofort für alle Mandanten.' });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Speichern hat nicht geklappt.') }),
  });
  const reset = useMutation({
    mutationFn: () => operatorApi('DELETE', '/plans'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['operator-plans'] });
      client.invalidateQueries({ queryKey: ['meta'] });
      setMessage({ ok: true, text: 'Die Standardzuordnung gilt wieder.' });
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Zurücksetzen hat nicht geklappt.') }),
  });

  return (
    <>
      <p><Link to="/betreiber" className="back-link">← Alle Mandanten</Link></p>
      <div className="page-head">
        <h1>Pakete und Module</h1>
      </div>
      <p className="muted">
        Hier legen Sie fest, welches Modul in welchem Paket steckt. Ein größeres Paket enthält immer alles aus den kleineren. Die Änderung wirkt sofort für alle Mandanten: Wandert ein Modul in ein größeres Paket,
        verlieren Mandanten des kleineren Pakets den Zugriff, ihre Daten bleiben erhalten. Benutzer und Einrichtung bleiben immer in Basic. Die Webseite übernimmt die Zuordnung beim Laden der Seite.
      </p>
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      {plans.isLoading && <p className="muted">Wird geladen …</p>}
      {plans.isError && <div className="alert alert--error" role="alert">Die Pakete konnten nicht geladen werden.</div>}

      {data && (
        <>
          <div className="card">
            <h2>Überblick</h2>
            <ul className="plans-sum">
              {PLANS.map((p) => (
                <li key={p}>
                  <strong>{PLAN_LABELS[p]}</strong>: {counts(p)} Module, {formatCents(PLAN_PRICES_GROSS_MONTH_CENTS[p])} brutto je Benutzer und Monat
                </li>
              ))}
            </ul>
          </div>

          <div className="table-wrap">
            <table className="table table--cards">
              <thead>
                <tr><th>Modul</th><th>Standard im Code</th><th>Gilt heute in</th></tr>
              </thead>
              <tbody>
                {data.modules.map((m) => {
                  const value = draft[m.key] ?? choiceOf(m.plan);
                  const changed = value !== choiceOf(m.plan);
                  return (
                    <tr key={m.key}>
                      <td>
                        <strong>{m.label}</strong>{!m.available && <span className="muted small"> (in Vorbereitung)</span>}
                        <div className="muted small">{m.description}</div>
                      </td>
                      <td data-label="Standard im Code">{m.defaultPlan ? PLAN_LABELS[m.defaultPlan] : 'Nur als Zusatzmodul'}</td>
                      <td data-label="Gilt in">
                        <select className="input" aria-label={`Paket für ${m.label}`} value={value} disabled={m.fixed} onChange={(e) => setDraft((d) => ({ ...d, [m.key]: e.target.value as Choice }))}>
                          {CHOICES.filter((c) => !m.fixed || c.value === 'basic').map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                        {changed && <div className="muted small">geändert</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {moved.length > 0 && (
            <div className="alert alert--warn" role="status">
              Ungespeichert: {moved.map((m) => `${m.label} → ${CHOICES.find((c) => c.value === draft[m.key])?.label}`).join(', ')}.
            </div>
          )}
          <div className="form-actions">
            <button type="button" className="btn btn--primary" disabled={!dirty || save.isPending} onClick={() => { setMessage(null); save.mutate(); }}>{save.isPending ? 'Wird gespeichert …' : 'Zuordnung speichern'}</button>
            <button type="button" className="btn btn--secondary" disabled={reset.isPending} onClick={() => { if (window.confirm('Alle Module wieder dem Paket aus dem Code zuordnen?')) { setMessage(null); reset.mutate(); } }}>Auf Standard zurücksetzen</button>
          </div>
          <p className="muted small">{data.updatedAt ? `Zuletzt geändert am ${dateTime.format(new Date(data.updatedAt))}${data.updatedBy ? ` von ${data.updatedBy}` : ''}.` : 'Es gilt die Standardzuordnung aus dem Code.'}</p>
        </>
      )}
    </>
  );
}
